import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Tabs,
  LoadingOverlay,
  Stack,
  Grid,
  ThemeIcon,
  Divider,
  Table,
  Card,
  Select,
  ScrollArea,
  Pagination,
  Center,
  TextInput,
  NumberInput,
  Textarea,
  Avatar,
  Modal,
  Switch,
  ColorInput,
  ColorSwatch,
  Tooltip,
  ActionIcon,
  Menu,
  SegmentedControl,
  rem,
  SimpleGrid,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconSettings,
  IconShoppingCart,
  IconTicket,
  IconChartBar,
  IconTag,
  IconClock,
  IconMapPin,
  IconCalendarTime,
  IconFilter,
  IconDeviceFloppy,
  IconTrash,
  IconUser,
  IconDotsVertical,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconVersions,
  IconEdit,
  IconPlus,
  IconCalendarEvent,
  IconRefresh,
  IconBolt,
  IconPhoto,
  IconUpload,
  IconStar,
  IconArrowRight,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";

// --- SUB-MODULE IMPORTS ---
// Ensure these paths match your folder structure exactly
import AddOnEventPasses from "./ActivityManangement/AddOnEventPasses";
import AddOnEventActivityManagementPage from "./ActivityManangement/AddOnEventActivityManagementPage/index";
import CampaignCouponManagement from "./ProductCouponManagement/CampaignCouponManagement";
import { API_BASE_URL } from "../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

// --- IST DATETIME HELPERS ---
// Event dates are stored in UTC, but <input type="datetime-local"> is
// timezone-naive — it takes whatever "YYYY-MM-DDTHH:mm" string it's given
// at face value with no offset. India is a single timezone (UTC+5:30, no
// DST), so we shift explicitly rather than relying on the browser's own
// timezone (an admin outside India would otherwise see/set the wrong time).
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// DB (UTC) -> value for the datetime-local input (IST wall-clock)
const toIstDatetimeLocal = (isoString) => {
  const istMs = new Date(isoString).getTime() + IST_OFFSET_MS;
  return new Date(istMs).toISOString().slice(0, 16);
};

// datetime-local input value (IST wall-clock) -> ISO string for the API (UTC)
const fromIstDatetimeLocal = (localValue) => {
  if (!localValue) return null;
  const utcMs = new Date(`${localValue}:00Z`).getTime() - IST_OFFSET_MS;
  return new Date(utcMs).toISOString();
};

function ImageUploadComponent({ onUpload }) {
  const [uploading, setUploading] = useState(false);

  const handleDrop = async (files) => {
    setUploading(true);
    try {
      const base64Promises = files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () =>
              resolve({ image: reader.result, description: file.name });
            reader.onerror = reject;
          }),
      );
      const imagesPayload = await Promise.all(base64Promises);
      const response = await fetch(
        `${API_BASE_URL}/auth/bulkUploadImages`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ images: imagesPayload }),
        },
      );
      const data = await response.json();
      if (data.results) {
        data.results.forEach((res) => {
          if (res.success && res.url) onUpload(res.url);
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box h="100%" w="100%" pos="relative">
      <LoadingOverlay visible={uploading} overlayProps={{ blur: 2 }} />
      <Dropzone
        onDrop={handleDrop}
        accept={IMAGE_MIME_TYPE}
        multiple
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "2px dashed var(--mantine-color-gray-4)",
          backgroundColor: "var(--mantine-color-gray-0)",
          borderRadius: 8,
          cursor: "pointer",
          padding: "10px",
        }}
      >
        <Stack align="center" gap={5} style={{ pointerEvents: "none" }}>
          <IconPhoto
            style={{
              width: rem(30),
              height: rem(30),
              color: "var(--mantine-color-dimmed)",
            }}
          />
          <Text size="xs" c="dimmed" ta="center" lh={1.2}>
            + Add Images
          </Text>
        </Stack>
      </Dropzone>
    </Box>
  );
}

// =============================================================================
// SUB-COMPONENT: EVENT TIMESLOT MANAGER
// =============================================================================
const EventTimeslotManager = ({ productId }) => {
  const [timeslots, setTimeslots] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useNotification();

  // Generator State
  const [generatorData, setGeneratorData] = useState({
    startDate: "",
    endDate: "",
    slotDurationMinutes: 30,
    capacityLimit: 50,
  });
  const [generating, setGenerating] = useState(false);

  // Edit State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);

  const fetchTimeslots = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/timeslots/${productId}`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        setTimeslots(data.data || []);
      }
    } catch (e) {
      console.error(e);
      showError("Error", "Failed to fetch timeslots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeslots();
  }, [productId]);

  const handleGenerate = async () => {
    if (!generatorData.startDate || !generatorData.endDate) {
      return showError("Missing Data", "Start and End dates are required.");
    }

    if (
      !window.confirm(
        "WARNING: Generating new timeslots will DELETE all existing un-booked slots for this event. Continue?",
      )
    )
      return;

    setGenerating(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/timeslots/generate/${productId}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(generatorData),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Generated", data.message);
        fetchTimeslots();
      } else {
        showError("Generation Failed", data.error || data.message);
      }
    } catch (e) {
      showError("Error", "Failed to generate timeslots");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (slotId) => {
    if (!window.confirm("Delete this timeslot?")) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/timeslots/${slotId}`,
        { method: "DELETE", headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Deleted", "Timeslot removed.");
        fetchTimeslots();
      } else {
        showError("Error", data.error);
      }
    } catch (e) {
      showError("Error", "Failed to delete slot.");
    }
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/timeslots/${editingSlot.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            capacityLimit: editingSlot.capacityLimit,
            isActive: editingSlot.isActive,
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Updated", "Timeslot updated successfully.");
        setEditModalOpen(false);
        fetchTimeslots();
      } else {
        showError("Error", data.error);
      }
    } catch (e) {
      showError("Error", "Failed to update slot.");
    }
  };

  const formatDateTime = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box mt="md">
      <Paper withBorder radius="md" p="md" bg="gray.0">
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconCalendarEvent size={20} color="var(--mantine-color-blue-6)" />
            <Text fw={700}>Timeslot Generator</Text>
          </Group>
          <Badge color="orange" variant="light">
            Event-Wide Setting
          </Badge>
        </Group>

        <Text size="xs" c="dimmed" mb="md">
          Timeslots are applied to the entire event. Generating new slots will
          replace existing ones (unless tickets are already sold).
        </Text>

        <Grid align="flex-end">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label="Start Date & Time"
              type="datetime-local"
              size="xs"
              value={generatorData.startDate}
              onChange={(e) =>
                setGeneratorData({
                  ...generatorData,
                  startDate: e.target.value,
                })
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label="End Date & Time"
              type="datetime-local"
              size="xs"
              value={generatorData.endDate}
              onChange={(e) =>
                setGeneratorData({ ...generatorData, endDate: e.target.value })
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 2 }}>
            <NumberInput
              label="Duration (mins)"
              size="xs"
              value={generatorData.slotDurationMinutes}
              onChange={(v) =>
                setGeneratorData({ ...generatorData, slotDurationMinutes: v })
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 2 }}>
            <NumberInput
              label="Capacity"
              size="xs"
              value={generatorData.capacityLimit}
              onChange={(v) =>
                setGeneratorData({ ...generatorData, capacityLimit: v })
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 2 }}>
            <Button
              fullWidth
              size="xs"
              color="blue"
              leftSection={<IconBolt size={14} />}
              onClick={handleGenerate}
              loading={generating}
            >
              Generate
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* GENERATED TIMESLOTS TABLE */}
      {timeslots.length > 0 && (
        <Card withBorder radius="md" p={0} mt="md">
          <Group
            justify="space-between"
            p="sm"
            bg="gray.1"
            style={{ borderBottom: "1px solid #eee" }}
          >
            <Text fw={600} size="sm">
              Active Timeslots ({timeslots.length})
            </Text>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={fetchTimeslots}
              loading={loading}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Group>
          <ScrollArea h={250} type="auto" offsetScrollbars>
            <Table striped highlightOnHover verticalSpacing="xs" fontSize="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Time Window</Table.Th>
                  <Table.Th>Sold / Cap</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th align="right">Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {timeslots.map((slot) => (
                  <Table.Tr key={slot.id}>
                    <Table.Td fw={500}>
                      {formatDateTime(slot.startTime)} -{" "}
                      {new Date(slot.endTime).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Table.Td>
                    <Table.Td>
                      <Text
                        fw={700}
                        c={
                          slot.currentSoldCount >= slot.capacityLimit
                            ? "red"
                            : "green"
                        }
                        span
                      >
                        {slot.currentSoldCount}
                      </Text>
                      {" / "}
                      {slot.capacityLimit}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="xs"
                        color={slot.isActive ? "green" : "red"}
                        variant="dot"
                      >
                        {slot.isActive ? "Active" : "Hidden"}
                      </Badge>
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="flex-end">
                        <ActionIcon
                          size="sm"
                          color="blue"
                          variant="subtle"
                          onClick={() => {
                            setEditingSlot({ ...slot });
                            setEditModalOpen(true);
                          }}
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon
                          size="sm"
                          color="red"
                          variant="subtle"
                          onClick={() => handleDelete(slot.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Card>
      )}

      {/* NESTED EDIT MODAL FOR A SINGLE TIMESLOT */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={<Text fw={700}>Edit Timeslot</Text>}
        zIndex={1000} // Ensures it floats above the Ticket Type modal
      >
        {editingSlot && (
          <Stack>
            <Text size="sm" fw={500}>
              {formatDateTime(editingSlot.startTime)} -{" "}
              {new Date(editingSlot.endTime).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
            <NumberInput
              label="Capacity Limit"
              required
              min={editingSlot.currentSoldCount} // Can't set capacity lower than sold tickets
              value={editingSlot.capacityLimit}
              onChange={(v) =>
                setEditingSlot({ ...editingSlot, capacityLimit: v })
              }
              description={`Currently sold: ${editingSlot.currentSoldCount}`}
            />
            <Switch
              label="Timeslot is Active"
              checked={editingSlot.isActive}
              onChange={(e) =>
                setEditingSlot({
                  ...editingSlot,
                  isActive: e.currentTarget.checked,
                })
              }
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Update Slot</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
};

// =============================================================================
// SUB-COMPONENT: EVENT TICKET TYPES MANAGER
// =============================================================================
const EventTicketTypesManager = ({ productId }) => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useNotification();

  // Form State
  const [currentTicket, setCurrentTicket] = useState({
    id: null,
    ticketType: "",
    title: "",
    unitPrice: "",
    ticketMaxQuantity: "",
    hasTimeslots: false,
    activityTitle: "",
    activityDescription: "",
    userLimitRestiction: false,
    perUserLimit: "",
    isActive: true,
  });

  const fetchTicketTypes = async () => {
    setLoading(true);
    try {
      // Adjust URL prefix (/admin) based on exactly where your router is mounted
      const res = await fetch(
        `${API_BASE_URL}/admin/products/ticketTypes/${productId}`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        setTicketTypes(data.data || []);
      }
    } catch (e) {
      console.error(e);
      showError("Error", "Failed to load ticket types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketTypes();
  }, [productId]);

  const openModal = (ticket = null) => {
    if (ticket) {
      setCurrentTicket({
        ...ticket,
        title: ticket.title || "",
        unitPrice: ticket.unitPrice !== null ? ticket.unitPrice : "",
        ticketMaxQuantity: ticket.ticketMaxQuantity || "",
        perUserLimit: ticket.perUserLimit || "",
        activityTitle: ticket.activityTitle || "",
        activityDescription: ticket.activityDescription || "",
      });
    } else {
      setCurrentTicket({
        id: null,
        ticketType: "",
        title: "",
        unitPrice: "",
        ticketMaxQuantity: "",
        hasTimeslots: false,
        activityTitle: "",
        activityDescription: "",
        userLimitRestiction: false,
        perUserLimit: "",
        isActive: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (
      !currentTicket.ticketType ||
      currentTicket.unitPrice === "" ||
      !currentTicket.ticketMaxQuantity
    ) {
      return showError(
        "Missing Data",
        "Type, Price, and Max Quantity are required",
      );
    }

    if (currentTicket.userLimitRestiction && !currentTicket.perUserLimit) {
      return showError("Missing Data", "Please specify the limit per user.");
    }

    setSaving(true);
    try {
      const method = currentTicket.id ? "PUT" : "POST";
      const url = currentTicket.id
        ? `${API_BASE_URL}/admin/products/ticketTypes/${currentTicket.id}`
        : `${API_BASE_URL}/admin/products/ticketTypes/${productId}`;

      // Clean up payload (convert strings to numbers where necessary)
      const payload = {
        ...currentTicket,
        unitPrice: Number(currentTicket.unitPrice),
        ticketMaxQuantity: Number(currentTicket.ticketMaxQuantity),
        perUserLimit: currentTicket.userLimitRestiction
          ? Number(currentTicket.perUserLimit)
          : null,
      };

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        showSuccess(
          "Success",
          `Ticket Type ${currentTicket.id ? "Updated" : "Created"}`,
        );
        setIsModalOpen(false);
        fetchTicketTypes();
      } else {
        showError("Error", data.error || "Operation failed");
      }
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Box>
          <Text fw={600} size="lg">
            Manage Ticket Tiers
          </Text>
          <Text size="sm" c="dimmed">
            Configure pricing, capacity, and limits for event tickets.
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openModal()}
        >
          Add Ticket Type
        </Button>
      </Group>

      <Card withBorder radius="md" p={0}>
        <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead bg="gray.1">
            <Table.Tr>
              <Table.Th>Ticket Tier</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Price</Table.Th>
              <Table.Th>Total Cap</Table.Th>
              <Table.Th>Sold</Table.Th>
              <Table.Th>Timeslots</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th align="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ticketTypes.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={8} align="center" py="xl">
                  <Text c="dimmed">No ticket types configured.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              ticketTypes.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td fw={600}>{t.ticketType}</Table.Td>
                  <Table.Td>
                    {t.title || (
                      <Text size="sm" c="dimmed" fs="italic">
                        No title set
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>₹{t.unitPrice}</Table.Td>
                  <Table.Td>{t.ticketMaxQuantity}</Table.Td>
                  <Table.Td
                    fw={700}
                    c={
                      t.ticketBoughtQuantity >= t.ticketMaxQuantity
                        ? "red"
                        : "green"
                    }
                  >
                    {t.ticketBoughtQuantity || 0}
                  </Table.Td>
                  <Table.Td>
                    {t.hasTimeslots ? (
                      <Badge color="blue" variant="light">
                        Yes
                      </Badge>
                    ) : (
                      <Text size="sm" c="dimmed">
                        No
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={t.isActive ? "green" : "red"}
                      size="sm"
                      variant="dot"
                    >
                      {t.isActive ? "Active" : "Hidden"}
                    </Badge>
                  </Table.Td>
                  <Table.Td align="right">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => openModal(t)}
                    >
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* TICKET TYPE MODAL */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <Text fw={700} size="lg">
            {currentTicket.id ? "Edit Ticket Type" : "New Ticket Type"}
          </Text>
        }
        size="lg"
      >
        <Stack>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              {/* UPDATED: Strict Enum Dropdown */}
              <Select
                label="Ticket Tier"
                placeholder="Select ticket type"
                required
                data={[
                  { value: "GENERAL", label: "General Admission" },
                  { value: "VIP", label: "VIP" },
                  { value: "UPGRADE_TO_VIP", label: "Upgrade to VIP" },
                  { value: "NEW_YEAR_OFFER", label: "New Year Offer" },
                  { value: "EARLY_BIRD", label: "Early Bird" },
                  { value: "GROUP_DISCOUNT", label: "Group Discount" },
                ]}
                value={currentTicket.ticketType}
                onChange={(val) =>
                  setCurrentTicket({ ...currentTicket, ticketType: val })
                }
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput
                label="Unit Price"
                placeholder="₹"
                required
                value={currentTicket.unitPrice}
                onChange={(v) =>
                  setCurrentTicket({ ...currentTicket, unitPrice: v })
                }
              />
            </Grid.Col>
          </Grid>

          <TextInput
            label="Title"
            description="Custom label shown to buyers instead of the tier name above, e.g. 'Early Bird — Day 1 Access'"
            placeholder="e.g. Early Bird — Day 1 Access"
            value={currentTicket.title}
            onChange={(e) =>
              setCurrentTicket({ ...currentTicket, title: e.target.value })
            }
          />

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput
                label="Max Capacity"
                description="Total tickets available for this tier"
                required
                value={currentTicket.ticketMaxQuantity}
                onChange={(v) =>
                  setCurrentTicket({ ...currentTicket, ticketMaxQuantity: v })
                }
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap={2} mt="xl">
                <Switch
                  label="Enable Timeslots"
                  description="Requires users to pick a time"
                  checked={currentTicket.hasTimeslots}
                  onChange={(e) =>
                    setCurrentTicket({
                      ...currentTicket,
                      hasTimeslots: e.currentTarget.checked,
                    })
                  }
                />
              </Stack>
            </Grid.Col>
          </Grid>

          {/* NEW: DYNAMIC TIMESLOT UI INJECTION */}
          {currentTicket.hasTimeslots && (
            <Box
              mt="xs"
              mb="sm"
              style={{
                borderLeft: "3px solid var(--mantine-color-blue-5)",
                paddingLeft: 16,
              }}
            >
              <EventTimeslotManager productId={productId} />
            </Box>
          )}

          <Divider
            my="sm"
            label="Restrictions & Status"
            labelPosition="center"
          />

          <Divider
            my="sm"
            label="Restrictions & Status"
            labelPosition="center"
          />

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Switch
                label="Restrict Per User"
                mb="xs"
                checked={currentTicket.userLimitRestiction}
                onChange={(e) =>
                  setCurrentTicket({
                    ...currentTicket,
                    userLimitRestiction: e.currentTarget.checked,
                  })
                }
              />
              <NumberInput
                placeholder="Max tickets per user"
                disabled={!currentTicket.userLimitRestiction}
                value={currentTicket.perUserLimit}
                onChange={(v) =>
                  setCurrentTicket({ ...currentTicket, perUserLimit: v })
                }
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Switch
                label="Tier is Active (Visible)"
                mt="xs"
                checked={currentTicket.isActive}
                onChange={(e) =>
                  setCurrentTicket({
                    ...currentTicket,
                    isActive: e.currentTarget.checked,
                  })
                }
              />
            </Grid.Col>
          </Grid>

          <Divider my="sm" label="Optional Details" labelPosition="center" />

          <TextInput
            label="Activity Title"
            placeholder="e.g. Backstage Meet & Greet"
            value={currentTicket.activityTitle}
            onChange={(e) =>
              setCurrentTicket({
                ...currentTicket,
                activityTitle: e.target.value,
              })
            }
          />
          <Textarea
            label="Activity Description"
            minRows={3}
            value={currentTicket.activityDescription}
            onChange={(e) =>
              setCurrentTicket({
                ...currentTicket,
                activityDescription: e.target.value,
              })
            }
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Ticket Type
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

const ProductVariantsManager = ({ productId }) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useNotification();

  // Form State
  const [currentVariant, setCurrentVariant] = useState({
    id: null,
    name: "",
    price: "",
    stock: 0,
    size: "", // NEW
    colorCode: "", // NEW
    isActive: true,
  });

  // ... fetchVariants function remains the same ...
  const fetchVariants = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/${productId}/variants`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        setVariants(data.data || []);
      }
    } catch (e) {
      console.error(e);
      showError("Error", "Failed to load variants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVariants();
  }, [productId]);

  // Handle Open Modal
  const openModal = (variant = null) => {
    if (variant) {
      setCurrentVariant({
        ...variant,
        price: variant.price ? variant.price.toString() : "",
        colorCode: variant.colorCode || "",
        size: variant.size || "",
      });
    } else {
      setCurrentVariant({
        id: null,
        name: "",
        price: "",
        stock: 0,
        size: "",
        colorCode: "#000000",
        isActive: true,
      });
    }
    setIsModalOpen(true);
  };

  // Handle Save
  const handleSave = async () => {
    if (!currentVariant.name)
      return showError("Missing Data", "Variant Name is required");

    setSaving(true);
    try {
      const method = currentVariant.id ? "PUT" : "POST";
      const url = currentVariant.id
        ? `${API_BASE_URL}/admin/${productId}/variants/${currentVariant.id}`
        : `${API_BASE_URL}/admin/${productId}/variants`;

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(currentVariant),
      });

      const data = await res.json();
      if (data.success) {
        showSuccess(
          "Success",
          `Variant ${currentVariant.id ? "Updated" : "Created"}`,
        );
        setIsModalOpen(false);
        fetchVariants();
      } else {
        showError("Error", data.error || "Operation failed");
      }
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setSaving(false);
    }
  };

  // ... handleDelete remains the same ...
  const handleDelete = async (variantId) => {
    if (!window.confirm("Delete this variant?")) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/${productId}/variants/${variantId}`,
        { method: "DELETE", headers: getAuthHeaders() },
      );
      if (res.ok) {
        showSuccess("Deleted", "Variant removed");
        fetchVariants();
      }
    } catch (e) {
      showError("Error", "Delete failed");
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Box>
          <Text fw={600} size="lg">
            Product Variants
          </Text>
          <Text size="sm" c="dimmed">
            Manage sizes, colors, and stock
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openModal()}
        >
          Add Variant
        </Button>
      </Group>

      <Card withBorder radius="md" p={0}>
        <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead bg="gray.1">
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Size</Table.Th>
              <Table.Th>Color</Table.Th>
              <Table.Th>Price</Table.Th>
              <Table.Th>Stock</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th align="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {variants.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7} align="center" py="xl">
                  <Text c="dimmed">No variants added.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              variants.map((v) => (
                <Table.Tr key={v.id}>
                  <Table.Td fw={500}>{v.name}</Table.Td>

                  {/* SIZE COLUMN */}
                  <Table.Td>
                    {v.size ? (
                      <Badge variant="outline" color="gray">
                        {v.size}
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </Table.Td>

                  {/* COLOR COLUMN */}
                  <Table.Td>
                    {v.colorCode ? (
                      <Group gap={6}>
                        <ColorSwatch color={v.colorCode} size={16} />
                        <Text size="xs" c="dimmed">
                          {v.colorCode}
                        </Text>
                      </Group>
                    ) : (
                      "-"
                    )}
                  </Table.Td>

                  <Table.Td>
                    {v.price ? (
                      `₹${parseFloat(v.price).toLocaleString()}`
                    ) : (
                      <Text size="xs" c="dimmed">
                        Base Price
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{v.stock}</Table.Td>

                  <Table.Td>
                    <Badge
                      color={v.isActive ? "green" : "red"}
                      size="sm"
                      variant="dot"
                    >
                      {v.isActive ? "Active" : "Hidden"}
                    </Badge>
                  </Table.Td>

                  <Table.Td align="right">
                    <Group gap="xs" justify="flex-end">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => openModal(v)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(v.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* ADD/EDIT MODAL */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <Text fw={700}>
            {currentVariant.id ? "Edit Variant" : "New Variant"}
          </Text>
        }
      >
        <Stack>
          <TextInput
            label="Variant Name"
            placeholder="e.g. Red Large T-Shirt"
            required
            value={currentVariant.name}
            onChange={(e) =>
              setCurrentVariant({ ...currentVariant, name: e.target.value })
            }
          />

          <Grid>
            {/* SIZE INPUT */}
            <Grid.Col span={6}>
              <TextInput
                label="Size"
                placeholder="e.g. XL, 42, 10"
                value={currentVariant.size}
                onChange={(e) =>
                  setCurrentVariant({ ...currentVariant, size: e.target.value })
                }
              />
            </Grid.Col>

            {/* COLOR INPUT (Mantine Color Picker) */}
            <Grid.Col span={6}>
              <ColorInput
                label="Color Code"
                placeholder="#FF0000"
                value={currentVariant.colorCode}
                onChange={(val) =>
                  setCurrentVariant({ ...currentVariant, colorCode: val })
                }
                swatches={[
                  "#25262b",
                  "#868e96",
                  "#fa5252",
                  "#e64980",
                  "#be4bdb",
                  "#7950f2",
                  "#4c6ef5",
                  "#228be6",
                  "#15aabf",
                  "#12b886",
                  "#40c057",
                  "#82c91e",
                  "#fab005",
                  "#fd7e14",
                ]}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <NumberInput
                label="Price Override (Optional)"
                description="Leave empty to use base price"
                placeholder="₹"
                value={currentVariant.price}
                onChange={(v) =>
                  setCurrentVariant({ ...currentVariant, price: v })
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label="Stock Quantity"
                required
                value={currentVariant.stock}
                onChange={(v) =>
                  setCurrentVariant({ ...currentVariant, stock: v })
                }
              />
            </Grid.Col>
          </Grid>

          <Switch
            label="Variant is Active"
            checked={currentVariant.isActive}
            onChange={(e) =>
              setCurrentVariant({
                ...currentVariant,
                isActive: e.currentTarget.checked,
              })
            }
            mt="xs"
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Variant
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

// =============================================================================
// SUB-COMPONENT: PRODUCT ORDERS TABLE (With Pagination)
// =============================================================================
const ProductOrdersTable = ({ productId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        orderStatus: filter,
        page: page,
        limit: pagination.limit,
      });

      const res = await fetch(
        `${API_BASE_URL}/admin/products/${productId}/orders?${query}`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();

      if (data.success) {
        setOrders(data.data.orders);
        // Assuming API returns pagination info in data.data.pagination
        if (data.data.pagination) {
          setPagination({
            ...pagination,
            page,
            totalPages: data.data.pagination.totalPages,
          });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 })); // Reset to page 1 on filter change
    fetchOrders(1);
  }, [productId, filter]);

  return (
    <Box>
      <Group mb="md" justify="space-between">
        <Text fw={600} size="lg">
          Orders History
        </Text>
        <Select
          placeholder="Filter Status"
          data={["PENDING", "CONFIRMED", "SHIPPED", "DELIVERED", "CANCELLED"]}
          value={filter}
          onChange={setFilter}
          clearable
          leftSection={<IconFilter size={16} />}
          w={200}
        />
      </Group>

      <Card withBorder radius="md" padding="0">
        <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />

        <ScrollArea>
          <Table stickyHeader striped highlightOnHover>
            <Table.Thead bg="gray.1">
              <Table.Tr>
                <Table.Th>Order ID</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {orders.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5} align="center" py="xl">
                    <Text c="dimmed">No orders found</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                orders.map((order) => (
                  <Table.Tr key={order.id}>
                    <Table.Td>
                      <Text
                        size="sm"
                        fw={500}
                        style={{ fontFamily: "monospace" }}
                      >
                        #{order.id.slice(-8)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Avatar size="sm" radius="xl" color="blue">
                          <IconUser size={14} />
                        </Avatar>
                        <Box>
                          <Text size="sm" fw={500}>
                            {order.buyer?.email}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {order.buyer?.nickname || "N/A"}
                          </Text>
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={600} size="sm">
                        ₹{parseFloat(order.totalAmount).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={
                          order.orderStatus === "DELIVERED"
                            ? "green"
                            : order.orderStatus === "CANCELLED"
                              ? "red"
                              : "blue"
                        }
                      >
                        {order.orderStatus}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <Center p="md" className="border-t">
            <Pagination
              total={pagination.totalPages}
              value={pagination.page}
              onChange={(p) => fetchOrders(p)}
            />
          </Center>
        )}
      </Card>
    </Box>
  );
};

// =============================================================================
// SUB-COMPONENT: PRODUCT TICKETS TABLE (With Pagination)
// =============================================================================
const ProductTicketsTable = ({ productId }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  //  Modal States
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchTickets = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: page,
        limit: pagination.limit,
      });

      const res = await fetch(
        `${API_BASE_URL}/products/${productId}/tickets?${query}`,
        { headers: getAuthHeaders() }, // Replace with your actual auth headers logic
      );
      const data = await res.json();

      if (data.success) {
        setTickets(data.data.tickets);
        if (data.data.pagination) {
          setPagination({
            ...pagination,
            page,
            totalPages: data.data.pagination.totalPages,
          });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(1);
  }, [productId]);

  
  const handleViewClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  return (
    <Box>
      <Text fw={600} size="lg" mb="md">
        Ticket Inventory
      </Text>
      <Card withBorder radius="md" padding="0">
        <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />

        <ScrollArea>
          <Table stickyHeader striped highlightOnHover>
            <Table.Thead bg="gray.1">
              <Table.Tr>
                <Table.Th>Ticket #</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tickets.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5} align="center" py="xl">
                    <Text c="dimmed">No tickets issued yet</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                tickets.map((t) => (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Text
                        size="sm"
                        fw={600}
                        style={{ fontFamily: "monospace" }}
                      >
                        {t.ticketNumber}
                      </Text>
                      {t.seatNumber && (
                        <Badge size="xs" variant="outline" mt={4}>
                          Seat: {t.seatNumber}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {t.user?.email}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {t.user?.phoneNumber || "No Phone"}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="dot" color="gray" size="sm">
                        {t.ticketType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={
                          t.status === "CONFIRMED"
                            ? "green"
                            : t.status === "USED"
                              ? "blue"
                              : "yellow"
                        }
                      >
                        {t.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {/*  Connected the button to the handler */}
                      <Button 
                        variant="subtle" 
                        size="xs" 
                        compact 
                        onClick={() => handleViewClick(t)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {pagination.totalPages > 1 && (
          <Center p="md" className="border-t">
            <Pagination
              total={pagination.totalPages}
              value={pagination.page}
              onChange={(p) => fetchTickets(p)}
            />
          </Center>
        )}
      </Card>

      {/* The Ticket Details Modal */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={<Text fw={700} size="lg">Ticket Details</Text>}
        size="lg"
        centered
        overlayProps={{ blur: 3 }}
      >
        {selectedTicket && (
          <Stack gap="md">
            {/* Ticket Info Section */}
            <Box>
              <Text c="dimmed" size="xs" tt="uppercase" fw={700} mb="xs">
                Pass Information
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Ticket Number</Text>
                  <Text fw={600} style={{ fontFamily: "monospace" }}>{selectedTicket.ticketNumber}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Status</Text>
                  <Badge 
                    color={selectedTicket.status === 'CONFIRMED' ? 'green' : selectedTicket.status === 'USED' ? 'blue' : 'yellow'}
                  >
                    {selectedTicket.status}
                  </Badge>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Type / Tier</Text>
                  <Text fw={500}>{selectedTicket.ticketType}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Total Paid</Text>
                  <Text fw={600}>₹{selectedTicket.totalAmount}</Text>
                </Grid.Col>
              </Grid>
            </Box>

            <Divider />

            {/* User Info Section */}
            <Box>
              <Text c="dimmed" size="xs" tt="uppercase" fw={700} mb="xs">
                Attendee Details
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Name</Text>
                  <Text fw={500}>{selectedTicket.user?.name}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Phone</Text>
                  <Text fw={500}>{selectedTicket.user?.phoneNumber}</Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text size="sm" c="dimmed">Email</Text>
                  <Text fw={500}>{selectedTicket.user?.email}</Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text size="sm" c="dimmed">Default Address</Text>
                  <Text fw={500}>{selectedTicket.user?.address}</Text>
                </Grid.Col>
              </Grid>
            </Box>

          </Stack>
        )}
      </Modal>

    </Box>
  );
};

// =============================================================================
// MAIN WORKSPACE COMPONENT
// =============================================================================
export default function ProductWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  // ?tab=coupons (or sales, ticketTypes...) opens that tab, used by links in Slack alerts
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");

  // Settings Form State
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [stockMode, setStockMode] = useState("add");
  const { showError, showSuccess } = useNotification();

  // --- FETCH PRODUCT ---
  const fetchProduct = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/${id}`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.success) {
        setProduct(data.data);
        // Initialize settings form
        setFormData({
          title: data.data.title || "",
          description: data.data.description || "",
          unitPrice: data.data.unitPrice,
          targetOrderQuantity: 0,
          absoluteStock: data.data.targetOrderQuantity || 0, // Value if setting
          images: data.data.media
            ? data.data.media
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((m) => m.url)
            : [],
          status: data.data.status,
          category: data.data.category || "",
          venue: data.data.venue || "",
          city: data.data.city || "",
          state: data.data.state || "",
          // Stored as UTC; datetime-local inputs are timezone-naive and take
          // whatever string we give them at face value, so shift to IST
          // wall-clock first or the admin sees a time 5:30 off from reality.
          dateTime: data.data.dateTime ? toIstDatetimeLocal(data.data.dateTime) : "",
        });
      } else navigate("/admin/dashboard/product-management");
    } catch (e) {
      showError("Error", "Failed to load product");
      navigate("/admin/dashboard/product-management");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const handleImageUpload = (url) => {
    if (url)
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), url],
      }));
  };
  const removeImage = (idx) =>
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  const setAsPrimary = (idx) => {
    if (idx === 0) return;
    setFormData((prev) => {
      const newImgs = [...prev.images];
      const [sel] = newImgs.splice(idx, 1);
      newImgs.unshift(sel);
      return { ...prev, images: newImgs };
    });
  };
  const moveImage = (idx, dir) => {
    setFormData((prev) => {
      const newImgs = [...prev.images];
      const tar = idx + dir;
      if (tar < 0 || tar >= newImgs.length) return prev;
      [newImgs[idx], newImgs[tar]] = [newImgs[tar], newImgs[idx]];
      return { ...prev, images: newImgs };
    });
  };
  // --- HANDLERS ---
  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await fetch(
        `${API_BASE_URL}/admin/products/${id}/status`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: newStatus }),
        },
      );
      showSuccess("Status Updated", `Product is now ${newStatus}`);
      fetchProduct();
    } catch (e) {
      showError("Error", "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    // 1. Determine the Delta (How much to send to backend)
    let delta = 0;
    if (stockMode === "add") {
      delta = Number(formData.targetOrderQuantity) || 0;
    } else {
      // If setting an absolute number, subtract the current stock to find the difference
      delta =
        (Number(formData.absoluteStock) || 0) -
        (product.targetOrderQuantity || 0);
    }

    // 2. Validation: Cannot drop below Sold items
    const newTotal = (product.targetOrderQuantity || 0) + delta;
    const sold = product.placedOrdersQuantity || 0;
    if (newTotal < sold) {
      return showError(
        "Invalid Stock",
        `Cannot reduce stock below the currently sold quantity (${sold}).`,
      );
    }

    // 3. Format Payload (Include Delta and formatted Images)
    const payload = {
      title: formData.title,
      description: formData.description,
      unitPrice: formData.unitPrice,
      category: formData.category,
      targetOrderQuantity: delta, // Backend automatically ADDS this value to current stock
      media: formData.images.map((url, index) => ({
        mediaType: "IMAGE",
        url: url,
        isPrimary: index === 0,
        displayOrder: index,
      })),
    };

    if (isEvent) {
      payload.venue = formData.venue || null;
      payload.city = formData.city || null;
      payload.state = formData.state || null;
      payload.dateTime = fromIstDatetimeLocal(formData.dateTime);
    }

    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/${id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Saved", "Product updated successfully");
        fetchProduct();
        setStockMode("add");
      } else {
        showError("Error", data.error?.message || "Failed to save settings");
      }
    } catch (e) {
      showError("Error", "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure? This cannot be undone.")) return;
    try {
      showSuccess("Deleted", "Product deleted (Simulated)");
      navigate("/admin/dashboard/product-management");
    } catch (e) {
      showError("Error", "Delete failed");
    }
  };

  if (loading && !product) return <LoadingOverlay visible />;
  if (!product) return <Text>Product not found</Text>;

  const isEvent = ["EVENT", "MOVIE_SCREENING"].includes(product.productType);

  const statusColors = {
    AVAILABLE: "green",
    OUT_OF_STOCK: "red",
    SUSPENDED: "yellow",
    TESTING: "blue",
    DISCONTINUED: "gray",
  };

  return (
    <Box>
      {/* HEADER */}
      <Group mb="lg" justify="space-between">
        <Group>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate("/admin/dashboard/product-management")}
          >
            Back
          </Button>
          <Divider orientation="vertical" />
          <Stack gap={0}>
            <Text fw={700} size="lg">
              {product.title}
            </Text>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                ID: {product.id}
              </Text>
              <Badge
                size="xs"
                variant="dot"
                color={statusColors[product.status]}
              >
                {product.status}
              </Badge>
            </Group>
          </Stack>
        </Group>

        {/* HEADER ACTIONS: STATUS */}
        <Group>
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <Button
                variant="light"
                color={statusColors[product.status]}
                rightSection={<IconDotsVertical size={16} />}
              >
                {product.status}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Change Status</Menu.Label>
              <Menu.Item
                leftSection={<IconCheck size={14} />}
                onClick={() => handleStatusChange("AVAILABLE")}
              >
                Set Available
              </Menu.Item>
              <Menu.Item
                leftSection={<IconX size={14} />}
                onClick={() => handleStatusChange("OUT_OF_STOCK")}
              >
                Set Out of Stock
              </Menu.Item>
              <Menu.Item
                leftSection={<IconAlertTriangle size={14} />}
                onClick={() => handleStatusChange("SUSPENDED")}
              >
                Suspend
              </Menu.Item>
              <Menu.Item
                leftSection={<IconAlertTriangle size={14} />}
                onClick={() => handleStatusChange("TESTING")}
              >
                Set for Testing
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* TABS */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        radius="md"
        keepMounted={false}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>

          <Tabs.Tab
            value="sales"
            leftSection={
              isEvent ? (
                <IconTicket size={16} />
              ) : (
                <IconShoppingCart size={16} />
              )
            }
          >
            {isEvent ? "Tickets & Sales" : "Orders"}
          </Tabs.Tab>

          {isEvent && (
            <>
              <Tabs.Tab
                value="activities"
                leftSection={<IconClock size={16} />}
              >
                Activities Config
              </Tabs.Tab>
              <Tabs.Tab value="passes" leftSection={<IconTicket size={16} />}>
                Add-On Sales
              </Tabs.Tab>
            </>
          )}

          {isEvent && (
            <Tabs.Tab
              value="ticketTypes"
              leftSection={<IconTicket size={16} />}
            >
              Ticket Types
            </Tabs.Tab>
          )}
          {!isEvent && (
            <Tabs.Tab value="variants" leftSection={<IconVersions size={16} />}>
              Variants
            </Tabs.Tab>
          )}

          {isEvent && (
            <Tabs.Tab value="coupons" leftSection={<IconTag size={16} />}>
              Coupons
            </Tabs.Tab>
          )}

          <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Paper
          p="xl"
          radius="md"
          withBorder
          mih={500}
          style={{ position: "relative" }}
        >
          {/* 1. OVERVIEW TAB */}
          <Tabs.Panel value="overview">
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Text size="lg" fw={600} mb="md">
                  Details
                </Text>
                <Paper bg="gray.0" p="md" radius="md" mb="lg">
                  <Text style={{ whiteSpace: "pre-wrap" }}>
                    {product.description}
                  </Text>
                </Paper>

                {isEvent && (
                  <Group gap="xl">
                    <Group>
                      <ThemeIcon variant="light" color="blue">
                        <IconMapPin size={18} />
                      </ThemeIcon>
                      <Box>
                        <Text size="xs" c="dimmed">
                          Venue
                        </Text>
                        <Text size="sm" fw={500}>
                          {product.venue || "TBA"}, {product.city}
                        </Text>
                      </Box>
                    </Group>
                    <Group>
                      <ThemeIcon variant="light" color="orange">
                        <IconCalendarTime size={18} />
                      </ThemeIcon>
                      <Box>
                        <Text size="xs" c="dimmed">
                          Event Date
                        </Text>
                        <Text size="sm" fw={500}>
                          {product.dateTime
                            ? new Date(product.dateTime).toLocaleString(
                                "en-IN",
                                { timeZone: "Asia/Kolkata" },
                              )
                            : "N/A"}
                        </Text>
                      </Box>
                    </Group>
                  </Group>
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack>
                  <Paper withBorder p="md" radius="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Performance
                    </Text>
                    <Group justify="space-between" mt="xs">
                      <Text>Total Revenue</Text>
                      <Text fw={700}>
                        ₹{product.stats?.totalRevenue?.toLocaleString() || 0}
                      </Text>
                    </Group>
                    <Group justify="space-between" mt={4}>
                      <Text>{isEvent ? "Tickets Sold" : "Units Sold"}</Text>
                      <Text fw={700}>
                        {isEvent
                          ? product.stats?.totalTickets
                          : product.stats?.paidQuantity}
                      </Text>
                    </Group>
                  </Paper>

                  <Paper withBorder p="md" radius="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Inventory
                    </Text>
                    <Group justify="space-between" mt="xs">
                      <Text>Total Capacity</Text>
                      <Text fw={700}>{product.targetOrderQuantity}</Text>
                    </Group>
                    <Group justify="space-between" mt={4}>
                      <Text>Remaining</Text>
                      <Text fw={700} c="green">
                        {product.availableQuantity}
                      </Text>
                    </Group>
                  </Paper>
                </Stack>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {isEvent && (
            <Tabs.Panel value="ticketTypes">
              <EventTicketTypesManager productId={id} />
            </Tabs.Panel>
          )}

          {!isEvent && (
            <Tabs.Panel value="variants">
              <ProductVariantsManager productId={id} />
            </Tabs.Panel>
          )}

          {/* 2. SALES TAB */}
          <Tabs.Panel value="sales">
            {isEvent ? (
              <ProductTicketsTable productId={id} />
            ) : (
              <ProductOrdersTable productId={id} />
            )}
          </Tabs.Panel>

          {/* 3. ACTIVITIES TAB (Events Only) */}
          <Tabs.Panel value="activities">
            <AddOnEventActivityManagementPage productId={id} />
          </Tabs.Panel>

          {/* 4. PASSES TAB (Events Only) */}
          <Tabs.Panel value="passes">
            <AddOnEventPasses productId={id} />
          </Tabs.Panel>

          {/* 5. COUPONS TAB */}
          {isEvent && (
            <Tabs.Panel value="coupons">
              <CampaignCouponManagement />
            </Tabs.Panel>
          )}

          {/* 6. SETTINGS TAB */}
          <Tabs.Panel value="settings">
            <Stack maxW={800}>
              <Text fw={600} size="lg">
                Edit Product
              </Text>

              {/* --- IMAGE MANAGER --- */}
              <Paper p="md" radius="md" withBorder>
                <Text fw={700} mb="md">
                  Product Images
                </Text>
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
                  {formData.images?.map((url, idx) => (
                    <Box
                      key={idx}
                      pos="relative"
                      h={140}
                      className="group"
                      style={{
                        borderRadius: 8,
                        overflow: "hidden",
                        border:
                          idx === 0
                            ? "2px solid var(--mantine-color-blue-6)"
                            : "1px solid #eee",
                      }}
                    >
                      <img
                        src={url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          padding: 8,
                        }}
                      >
                        <Group justify="flex-end">
                          <ActionIcon
                            color="red"
                            variant="filled"
                            size="sm"
                            onClick={() => removeImage(idx)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                        {idx !== 0 && (
                          <Center>
                            <Button
                              size="xs"
                              variant="white"
                              radius="xl"
                              onClick={() => setAsPrimary(idx)}
                              style={{ height: 24, fontSize: 10 }}
                            >
                              Set Primary
                            </Button>
                          </Center>
                        )}
                        <Group justify="space-between">
                          <ActionIcon
                            variant="transparent"
                            c="white"
                            disabled={idx === 0}
                            onClick={() => moveImage(idx, -1)}
                          >
                            <IconArrowLeft size={18} />
                          </ActionIcon>
                          <ActionIcon
                            variant="transparent"
                            c="white"
                            disabled={idx === formData.images.length - 1}
                            onClick={() => moveImage(idx, 1)}
                          >
                            <IconArrowRight size={18} />
                          </ActionIcon>
                        </Group>
                      </Box>
                      {idx === 0 && (
                        <Box
                          pos="absolute"
                          bottom={0}
                          left={0}
                          right={0}
                          bg="blue.6"
                          c="white"
                          ta="center"
                          fz={10}
                          fw={700}
                          className="group-hover:hidden"
                        >
                          PRIMARY
                        </Box>
                      )}
                    </Box>
                  ))}
                  <Box h={140}>
                    <ImageUploadComponent onUpload={handleImageUpload} />
                  </Box>
                </SimpleGrid>
              </Paper>

              <TextInput
                label="Title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
              <Textarea
                label="Description"
                minRows={4}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />

              <Group grow align="flex-start">
                <NumberInput
                  label="Price (₹)"
                  value={formData.unitPrice}
                  onChange={(v) => setFormData({ ...formData, unitPrice: v })}
                />
                <TextInput
                  label="Category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                />
              </Group>

              {isEvent && (
                <Paper p="md" radius="md" withBorder>
                  <Text fw={700} mb="md">
                    Event Details
                  </Text>
                  <Group grow align="flex-start">
                    <TextInput
                      label="Venue"
                      placeholder="e.g. KTPO Trade Centre"
                      leftSection={<IconMapPin size={16} />}
                      value={formData.venue}
                      onChange={(e) =>
                        setFormData({ ...formData, venue: e.target.value })
                      }
                    />
                    <TextInput
                      label="Event Date & Time"
                      description="IST — shown and saved in India Standard Time"
                      type="datetime-local"
                      leftSection={<IconCalendarEvent size={16} />}
                      value={formData.dateTime}
                      onChange={(e) =>
                        setFormData({ ...formData, dateTime: e.target.value })
                      }
                    />
                  </Group>
                  <Group grow align="flex-start" mt="md">
                    <TextInput
                      label="City"
                      placeholder="e.g. Bengaluru"
                      value={formData.city}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                    />
                    <TextInput
                      label="State"
                      placeholder="e.g. Karnataka"
                      value={formData.state}
                      onChange={(e) =>
                        setFormData({ ...formData, state: e.target.value })
                      }
                    />
                  </Group>
                  <Text size="xs" c="dimmed" mt="xs">
                    The Overview tab shows Venue and City together as "
                    {formData.venue || "Venue"}, {formData.city || "City"}".
                  </Text>
                </Paper>
              )}

              {/* --- ADVANCED STOCK INVENTORY --- */}
              <Paper p="md" radius="md" withBorder bg="gray.0">
                <Group justify="space-between" mb="xs">
                  <Text fw={700}>Inventory Management</Text>
                  <SegmentedControl
                    size="sm"
                    value={stockMode}
                    onChange={setStockMode}
                    data={[
                      { label: "Add Stock", value: "add" },
                      { label: "Reset Total", value: "set" },
                    ]}
                  />
                </Group>

                {stockMode === "add" ? (
                  <Box>
                    <NumberInput
                      label="Add to Total Capacity"
                      description="Enter how many new items/tickets to add to the existing inventory."
                      placeholder="0"
                      value={formData.targetOrderQuantity}
                      onChange={(v) =>
                        setFormData({
                          ...formData,
                          targetOrderQuantity: v === "" ? 0 : v,
                        })
                      }
                    />
                    <Group
                      justify="space-between"
                      mt="sm"
                      px="sm"
                      py={8}
                      bg="white"
                      style={{
                        borderRadius: 6,
                        border: "1px solid var(--mantine-color-gray-3)",
                      }}
                    >
                      <Text size="xs" fw={600} c="dimmed">
                        Current Total: {product.targetOrderQuantity || 0}
                      </Text>
                      <Text
                        size="xs"
                        fw={800}
                        c={
                          formData.targetOrderQuantity > 0
                            ? "green.6"
                            : formData.targetOrderQuantity < 0
                              ? "red.6"
                              : "dimmed"
                        }
                      >
                        New Total:{" "}
                        {(product.targetOrderQuantity || 0) +
                          (Number(formData.targetOrderQuantity) || 0)}
                      </Text>
                    </Group>
                  </Box>
                ) : (
                  <Box>
                    <NumberInput
                      label="Set Exact Absolute Stock"
                      description={`Must be greater than currently sold amount (${product.placedOrdersQuantity || 0}).`}
                      placeholder="Enter absolute total"
                      min={product.placedOrdersQuantity || 0}
                      value={formData.absoluteStock}
                      onChange={(v) =>
                        setFormData({
                          ...formData,
                          absoluteStock: v === "" ? 0 : v,
                        })
                      }
                    />
                    <Group
                      justify="space-between"
                      mt="sm"
                      px="sm"
                      py={8}
                      bg="white"
                      style={{
                        borderRadius: 6,
                        border: "1px solid var(--mantine-color-gray-3)",
                      }}
                    >
                      <Text size="xs" fw={600} c="dimmed">
                        Sold: {product.placedOrdersQuantity || 0}
                      </Text>
                      <Text
                        size="xs"
                        fw={800}
                        c={
                          Number(formData.absoluteStock) <
                          (product.placedOrdersQuantity || 0)
                            ? "red.6"
                            : "green.6"
                        }
                      >
                        Available to Sell:{" "}
                        {Math.max(
                          0,
                          (Number(formData.absoluteStock) || 0) -
                            (product.placedOrdersQuantity || 0),
                        )}
                      </Text>
                    </Group>
                  </Box>
                )}
              </Paper>

              <Group justify="flex-end" mt="md">
                <Button
                  leftSection={<IconDeviceFloppy size={18} />}
                  onClick={handleSaveSettings}
                  loading={saving}
                >
                  Save Changes
                </Button>
              </Group>

              <Divider
                label="Danger Zone"
                labelPosition="center"
                color="red"
                my="xl"
              />
              <Button
                color="red"
                variant="outline"
                onClick={handleDelete}
                leftSection={<IconTrash size={18} />}
              >
                Delete Product
              </Button>
            </Stack>
          </Tabs.Panel>
        </Paper>
      </Tabs>
    </Box>
  );
}
